// ===========================================
// P&L STATEMENT  (route /pnl)
// ===========================================
// Per-store profit & loss and an all-stores roll-up, for a selected period.
//   Revenue
//   − COGS (per category)            subtotal + gross margin %
//   − Operating expenses (per cat)   subtotal
//   = Net profit                     + net margin %
//
// Revenue is Toast net sales (reused from the API). Expenses come from the
// store's invoices, grouped vendor → category via an editable, account-shared
// map. Labor and any correction are editable manual lines. Everything is
// legible: sales-tax remittance is shown in a collapsed "excluded" note,
// unmatched vendors are surfaced so the operator can recategorize them.

import { useMemo, useState } from 'react';
import { useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pnlApi, restaurantApi } from '../services/api';
import PnlCpgPage from './PnlCpgPage';
import {
  ComparePeriodBar,
  CoverageBanner,
  Delta,
  GatedCompareRow,
  NoData,
  rangeLabel,
  type ComparePair,
  type DateRange,
  type DetailLine,
} from '../components/PeriodCompare';
import { GroupedBars, PairedBars, barColors } from '../components/analytics/Bars';
import { toast } from '../stores/toastStore';
import {
  Calendar, Printer, Plus, Pencil, Trash2, Tags, Info,
  AlertTriangle, Building2, Loader2, X,
} from 'lucide-react';
import clsx from 'clsx';

// ---------- types (mirror services/pnl.ts) ----------
type CategoryGroup = 'cogs' | 'opex';
interface PnlCategory { key: string; label: string; group: CategoryGroup; order: number }
interface PnlLine { category: string; label: string; amountCents: number; sourceNote: string }
interface ManualLine { id: string; category: string; label: string; amountCents: number; periodKey?: string }
interface ExcludedEntry { vendorName: string; amountCents: number; invoiceCount: number; reason: string }
interface UncategorizedVendor { normalizedName: string; vendorName: string; amountCents: number; invoiceCount: number }
interface PnlStatement {
  scope: { restaurantIds: string[]; restaurantNames: string[]; isRollup: boolean; start: string; end: string; rangeDays: number; periodKey?: string };
  revenueCents: number; revenueSourceNote: string;
  cogs: PnlLine[]; cogsTotalCents: number;
  grossProfitCents: number; grossMarginPct: number | null;
  opex: PnlLine[]; opexTotalCents: number;
  netCents: number; netMarginPct: number | null;
  excluded: ExcludedEntry[]; excludedTotalCents: number;
  uncategorizedVendors: UncategorizedVendor[];
  sourceCounts?: { salesDays: number; invoices: number; manualLines: number };
}

// "No data" means no underlying records at all — distinct from a real zero.
const pnlNoData = (st: PnlStatement | null | undefined) =>
  !!st?.sourceCounts && st.sourceCounts.salesDays === 0 && st.sourceCounts.invoices === 0 && st.sourceCounts.manualLines === 0;

// Per-metric coverage, read from PR #19's sourceCounts — NOT from the dollar
// value (a real $0 is data; salesDays:0 is not). Revenue and every margin line
// flow from sales; COGS and OpEx flow from invoices + manual lines.
const hasSales = (st: PnlStatement | null | undefined) => (st?.sourceCounts?.salesDays ?? 0) > 0;
const hasExpenses = (st: PnlStatement | null | undefined) =>
  ((st?.sourceCounts?.invoices ?? 0) + (st?.sourceCounts?.manualLines ?? 0)) > 0;

// The few largest OpEx categories for a period, as a compact "label $amount"
// string — so a big or a tiny operating-expenses figure is explained.
function topOpexLabel(st: PnlStatement | null | undefined, n = 3): string {
  const rows = (st?.opex || []).filter((l) => l.amountCents !== 0).sort((a, b) => b.amountCents - a.amountCents).slice(0, n);
  if (rows.length === 0) return 'no categories';
  return rows.map((l) => `${l.label} ${money(l.amountCents)}`).join(', ');
}

interface VendorRow { normalizedName: string; name: string; vendorIds: string[]; category: string | null; categorySource: 'saved' | 'heuristic' | 'unmatched' }

type PeriodChoice = 'thisWeek' | 'thisMonth' | 'lastMonth' | 'custom';

// ---------- helpers ----------
function money(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100);
}
function pct(v: number | null): string {
  return v === null ? '—' : `${v.toFixed(1)}%`;
}
function getStoredRestaurants(): Array<{ id: string; name: string }> {
  try { return JSON.parse(localStorage.getItem('manager_restaurants') || '[]'); } catch { return []; }
}
function getCurrentRestaurantId(): string | null {
  try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null')?.id || null; } catch { return null; }
}

// CPG accounts get the monthly build-down (gross revenue → deductions → gross
// profit, from wholesale orders); everyone else keeps the store statement
// below, exactly as before. Detection reads the same signals Layout.tsx keys
// off, through the same query key, so no extra request fires.
export default function PnlPage() {
  const currentRestaurantId = getCurrentRestaurantId();
  const { data: fullRestaurant, isPending } = useQuery({
    queryKey: ['restaurant-current', currentRestaurantId],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
  const rs = (fullRestaurant?.settings as any) || {};
  const isCpgAccount = rs.businessType === 'cpg' || rs.businessKind === 'cpg' || !!rs.supplyRatios || !!rs.channelForecast;

  if (isPending) {
    return (
      <div className="p-4 md:p-6">
        <div className="card p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" /><p className="mt-4 text-slate-500">Opening the statement…</p></div>
      </div>
    );
  }
  return isCpgAccount ? <PnlCpgPage /> : <RestaurantPnlPage />;
}

function RestaurantPnlPage() {
  const qc = useQueryClient();
  const stores = useMemo(getStoredRestaurants, []);
  const isMultiStore = stores.length > 1;

  // Scope: a store id, or 'all' for the roll-up (default to roll-up when multi-store).
  const [scope, setScope] = useState<string>(() => (isMultiStore ? 'all' : (getCurrentRestaurantId() || stores[0]?.id || 'all')));
  const [period, setPeriod] = useState<PeriodChoice>('thisMonth');
  const [custom, setCustom] = useState<{ start: string; end: string }>(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: iso(first), end: iso(now) };
  });

  const [showCategorize, setShowCategorize] = useState(false);
  const [editingLine, setEditingLine] = useState<ManualLine | null>(null);
  const [addingLine, setAddingLine] = useState(false);

  // --- statement ---
  const statementParams = useMemo(() => {
    const base: any = { restaurantId: scope };
    if (period === 'custom') { base.start = custom.start; base.end = custom.end; }
    else base.period = period;
    return base;
  }, [scope, period, custom]);

  const { data: statement, isLoading, isError } = useQuery<PnlStatement>({
    queryKey: ['pnl-statement', statementParams],
    queryFn: async () => {
      const res = await pnlApi.getStatement(statementParams);
      return res.data?.data || res.data;
    },
  });

  // --- config (categories + this store's manual lines) ---
  const { data: config } = useQuery<{ categories: PnlCategory[]; vendorCategories: Record<string, string>; manualLines: ManualLine[]; hubRestaurantId: string }>({
    queryKey: ['pnl-config', scope],
    queryFn: async () => {
      const res = await pnlApi.getConfig();
      return res.data?.data || res.data;
    },
  });
  const categories = config?.categories || [];
  const manualLines = config?.manualLines || [];

  // --- period comparison: two explicit stored ranges, same scope ---
  const [pair, setPair] = useState<ComparePair | null>(null);
  // periodKey keeps period-scoped manual lines attached to their month, the
  // same way the named periods resolve it server-side.
  const compareParams = (r: DateRange) => ({ restaurantId: scope, start: r.start, end: r.end, periodKey: r.start.slice(0, 7) });
  const { data: cmpA } = useQuery<PnlStatement>({
    queryKey: ['pnl-compare', 'a', scope, pair],
    enabled: !!pair,
    queryFn: async () => {
      const res = await pnlApi.getStatement(compareParams(pair!.a));
      return res.data?.data || res.data;
    },
  });
  const { data: cmpB } = useQuery<PnlStatement>({
    queryKey: ['pnl-compare', 'b', scope, pair],
    enabled: !!pair,
    queryFn: async () => {
      const res = await pnlApi.getStatement(compareParams(pair!.b));
      return res.data?.data || res.data;
    },
  });

  // --- store comparison: one statement per store, side by side ---
  const storeStatementsA = useQueries({
    queries: (isMultiStore ? stores : []).map((s) => ({
      queryKey: ['pnl-statement-store', 'a', s.id, pair ? pair.a : statementParams],
      queryFn: async () => {
        const params = pair ? { ...compareParams(pair.a), restaurantId: s.id } : { ...statementParams, restaurantId: s.id };
        const res = await pnlApi.getStatement(params);
        return (res.data?.data || res.data) as PnlStatement;
      },
    })),
  });
  const storeStatementsB = useQueries({
    queries: (isMultiStore && pair ? stores : []).map((s) => ({
      queryKey: ['pnl-statement-store', 'b', s.id, pair?.b],
      queryFn: async () => {
        const res = await pnlApi.getStatement({ ...compareParams(pair!.b), restaurantId: s.id });
        return (res.data?.data || res.data) as PnlStatement;
      },
    })),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['pnl-statement'] });
    qc.invalidateQueries({ queryKey: ['pnl-config'] });
    qc.invalidateQueries({ queryKey: ['pnl-vendors'] });
  };

  // --- manual-line mutations ---
  const addLine = useMutation({
    mutationFn: (d: { category: string; label: string; amountCents: number; periodKey?: string }) => pnlApi.addManualLine(d),
    onSuccess: () => { invalidate(); setAddingLine(false); toast.success('Line added'); },
    onError: () => toast.error('Could not add that line'),
  });
  const updateLine = useMutation({
    mutationFn: (p: { id: string; d: any }) => pnlApi.updateManualLine(p.id, p.d),
    onSuccess: () => { invalidate(); setEditingLine(null); toast.success('Line updated'); },
    onError: () => toast.error('Could not update that line'),
  });
  const deleteLine = useMutation({
    mutationFn: (id: string) => pnlApi.deleteManualLine(id),
    onSuccess: () => { invalidate(); toast.success('Line removed'); },
    onError: () => toast.error('Could not remove that line'),
  });

  // Manual lines on the current single store, grouped by category (editing is
  // only offered on a single store, not the roll-up).
  const manualByCat = useMemo(() => {
    const m = new Map<string, ManualLine[]>();
    for (const l of manualLines) { const arr = m.get(l.category) || []; arr.push(l); m.set(l.category, arr); }
    return m;
  }, [manualLines]);

  // By-store chart rows — only stores whose statement loaded with real data,
  // in dollars (the chart) while the table beneath keeps the exact figures.
  const storeChartRows = stores
    .map((s, i) => ({ name: s.name, st: storeStatementsA[i]?.data }))
    .filter((x): x is { name: string; st: PnlStatement } => !!x.st && !pnlNoData(x.st))
    .map((x) => ({ store: x.name, revenue: x.st.revenueCents / 100, net: x.st.netCents / 100 }));

  const periodLabel = period === 'thisWeek' ? 'This week' : period === 'thisMonth' ? 'This month' : period === 'lastMonth' ? 'Last month' : 'Custom range';
  const scopeLabel = scope === 'all' ? `All stores (${stores.length})` : (stores.find((s) => s.id === scope)?.name || 'Store');
  const canEditManual = scope !== 'all';

  return (
    <div className="p-4 md:p-6 space-y-5 print:p-0">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .pnl-sheet { box-shadow: none !important; border: none !important; }
          @page { margin: 14mm; }
        }
      `}</style>

      {/* Header + controls */}
      <div className="no-print flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">P&amp;L Statement</h1>
          <p className="text-slate-500 text-sm">Profit &amp; loss per store, with an all-stores roll-up.</p>
        </div>
        <button onClick={() => window.print()} className="btn btn-secondary inline-flex items-center gap-2">
          <Printer className="w-4 h-4" /> Print / PDF
        </button>
      </div>

      {/* Scope + period picker */}
      <div className="no-print card p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-slate-500" />
          <select value={scope} onChange={(e) => setScope(e.target.value)} className="input w-auto">
            {isMultiStore && <option value="all">All stores (roll-up)</option>}
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-slate-500" />
          <div className="flex gap-1">
            {([['thisWeek', 'This week'], ['thisMonth', 'This month'], ['lastMonth', 'Last month'], ['custom', 'Custom']] as [PeriodChoice, string][]).map(([k, label]) => (
              <button key={k} onClick={() => setPeriod(k)}
                className={clsx('px-3 py-1.5 rounded-lg text-sm font-medium transition-colors', period === k ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="input w-auto" />
            <span className="text-slate-500">to</span>
            <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="input w-auto" />
          </div>
        )}
        <button onClick={() => setShowCategorize(true)} className="btn btn-secondary inline-flex items-center gap-2 ml-auto">
          <Tags className="w-4 h-4" /> Categorize spend
        </button>
      </div>

      {/* Period comparison — two real stored ranges on the selected scope */}
      <div className="no-print">
        <ComparePeriodBar onChange={setPair} />
      </div>
      {pair && (
        <div className="no-print card max-w-3xl mx-auto overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">Period comparison · {scopeLabel}</h2>
            <span className="text-sm text-slate-500">
              {rangeLabel(pair.a)} <span className="text-slate-400">vs</span> {rangeLabel(pair.b)}
            </span>
          </div>
          {!cmpA || !cmpB ? (
            <p className="text-sm text-slate-500 p-6">Loading both periods…</p>
          ) : pnlNoData(cmpA) || pnlNoData(cmpB) ? (
            <div className="p-6 space-y-1">
              {pnlNoData(cmpA) && <p><NoData range={pair.a} /></p>}
              {pnlNoData(cmpB) && <p><NoData range={pair.b} /></p>}
              <p className="text-xs text-slate-400 pt-1">
                Deltas only show when both periods have recorded sales, invoices, or manual lines — nothing is estimated or backfilled.
              </p>
            </div>
          ) : (
            (() => {
              // Per-metric coverage from PR #19 sourceCounts. Sales drive
              // revenue + every margin; invoices + manual lines drive COGS/OpEx.
              const aSales = hasSales(cmpA), bSales = hasSales(cmpB);
              const aExp = hasExpenses(cmpA), bExp = hasExpenses(cmpB);
              // Gross profit mixes the two (revenue − COGS) and net adds OpEx;
              // a delta is only honest when BOTH families cover BOTH periods.
              const aBlend = aSales && aExp, bBlend = bSales && bExp;
              // Bars render only for metrics whose both sides have data — a
              // gated metric passes null and PairedBars omits its bar.
              const barOrNull = (ok: boolean, v: number | null) => (ok ? v : null);
              const salesDetail: DetailLine[] = [{
                label: 'Sales days counted',
                a: `${cmpA.sourceCounts?.salesDays ?? 0} days`,
                b: `${cmpB.sourceCounts?.salesDays ?? 0} days`,
              }];
              const cogsDetail: DetailLine[] = [{
                label: 'Invoices + manual lines',
                a: `${cmpA.sourceCounts?.invoices ?? 0} inv · ${cmpA.sourceCounts?.manualLines ?? 0} manual`,
                b: `${cmpB.sourceCounts?.invoices ?? 0} inv · ${cmpB.sourceCounts?.manualLines ?? 0} manual`,
              }];
              const opexDetail: DetailLine[] = [
                {
                  label: 'Invoices + manual lines',
                  a: `${cmpA.sourceCounts?.invoices ?? 0} inv · ${cmpA.sourceCounts?.manualLines ?? 0} manual`,
                  b: `${cmpB.sourceCounts?.invoices ?? 0} inv · ${cmpB.sourceCounts?.manualLines ?? 0} manual`,
                },
                { label: 'Largest categories', a: topOpexLabel(cmpA), b: topOpexLabel(cmpB) },
              ];
              return (
                <>
                  <CoverageBanner
                    a={{ sales: cmpA.sourceCounts?.salesDays ?? 0, expense: (cmpA.sourceCounts?.invoices ?? 0) + (cmpA.sourceCounts?.manualLines ?? 0) }}
                    b={{ sales: cmpB.sourceCounts?.salesDays ?? 0, expense: (cmpB.sourceCounts?.invoices ?? 0) + (cmpB.sourceCounts?.manualLines ?? 0) }}
                    aRange={pair.a}
                    bRange={pair.b}
                    salesNoun="sales"
                    expenseNoun="expense"
                  />
                  <div className="p-4">
                    <PairedBars
                      aLabel={rangeLabel(pair.a)}
                      bLabel={rangeLabel(pair.b)}
                      metrics={[
                        { label: 'Revenue', a: barOrNull(aSales, cmpA.revenueCents), b: barOrNull(bSales, cmpB.revenueCents), kind: 'cents', goodWhenUp: true },
                        { label: 'Total COGS', a: barOrNull(aExp, cmpA.cogsTotalCents), b: barOrNull(bExp, cmpB.cogsTotalCents), kind: 'cents', goodWhenUp: false },
                        { label: 'Gross profit', a: barOrNull(aBlend, cmpA.grossProfitCents), b: barOrNull(bBlend, cmpB.grossProfitCents), kind: 'cents', goodWhenUp: true },
                        { label: 'Operating expenses', a: barOrNull(aExp, cmpA.opexTotalCents), b: barOrNull(bExp, cmpB.opexTotalCents), kind: 'cents', goodWhenUp: false },
                        { label: 'Net profit', a: barOrNull(aBlend, cmpA.netCents), b: barOrNull(bBlend, cmpB.netCents), kind: 'cents', goodWhenUp: true },
                      ]}
                    />
                  </div>
                  <p className="text-xs text-slate-400 px-4 -mt-2 pb-2">Select a line to see the records behind it.</p>
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="table-header">Line</th>
                        <th className="table-header text-right">{rangeLabel(pair.a)}</th>
                        <th className="table-header text-right">{rangeLabel(pair.b)}</th>
                        <th className="table-header text-right">Change</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <GatedCompareRow label="Revenue" a={cmpA.revenueCents} b={cmpB.revenueCents} kind="cents" goodWhenUp
                        aHasData={aSales} bHasData={bSales} noun="sales" aRange={pair.a} bRange={pair.b} detail={salesDetail} />
                      <GatedCompareRow label="Total COGS" a={cmpA.cogsTotalCents} b={cmpB.cogsTotalCents} kind="cents" goodWhenUp={false}
                        aHasData={aExp} bHasData={bExp} noun="invoices or manual lines" aRange={pair.a} bRange={pair.b} detail={cogsDetail} />
                      <GatedCompareRow label="Gross profit" a={cmpA.grossProfitCents} b={cmpB.grossProfitCents} kind="cents" goodWhenUp
                        aHasData={aBlend} bHasData={bBlend} noun="sales and expenses together" aRange={pair.a} bRange={pair.b} />
                      <GatedCompareRow label="Gross margin" a={cmpA.grossMarginPct} b={cmpB.grossMarginPct} kind="points" goodWhenUp
                        aHasData={aBlend} bHasData={bBlend} noun="sales and expenses together" aRange={pair.a} bRange={pair.b} />
                      <GatedCompareRow label="Operating expenses" a={cmpA.opexTotalCents} b={cmpB.opexTotalCents} kind="cents" goodWhenUp={false}
                        aHasData={aExp} bHasData={bExp} noun="invoices or manual lines" aRange={pair.a} bRange={pair.b} detail={opexDetail} />
                      <GatedCompareRow label="Net profit" a={cmpA.netCents} b={cmpB.netCents} kind="cents" goodWhenUp strong
                        aHasData={aBlend} bHasData={bBlend} noun="sales and expenses together" aRange={pair.a} bRange={pair.b} />
                      <GatedCompareRow label="Net margin" a={cmpA.netMarginPct} b={cmpB.netMarginPct} kind="points" goodWhenUp
                        aHasData={aBlend} bHasData={bBlend} noun="sales and expenses together" aRange={pair.a} bRange={pair.b} />
                    </tbody>
                  </table>
                  </div>
                </>
              );
            })()
          )}
        </div>
      )}

      {isLoading ? (
        <div className="card p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" /><p className="mt-4 text-slate-500">Building the statement…</p></div>
      ) : isError || !statement ? (
        <div className="card p-8 text-center text-slate-500">We couldn't build this statement right now. Try another period.</div>
      ) : (
        <div className="pnl-sheet card max-w-3xl mx-auto overflow-hidden">
          {/* Statement header */}
          <div className="px-6 pt-6 pb-4 border-b border-slate-200">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg font-bold text-slate-900">{scopeLabel}</h2>
              <span className="text-sm text-slate-500">{periodLabel}</span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{statement.scope.start} → {statement.scope.end} · {statement.scope.rangeDays} days</p>
          </div>

          <div className="px-6 py-4 space-y-5">
            {/* Revenue */}
            <section>
              <Row label="Revenue" amount={statement.revenueCents} strong note={statement.revenueSourceNote} />
            </section>

            {/* COGS */}
            <section>
              <GroupHeader title="Cost of goods sold" />
              {statement.cogs.map((l) => (
                <CategoryRow key={l.category} line={l} manual={manualByCat.get(l.category)} canEdit={canEditManual}
                  onAdd={() => { setAddingLine(true); setEditingLine({ id: '', category: l.category, label: '', amountCents: 0 }); }}
                  onEdit={(ml) => { setAddingLine(false); setEditingLine(ml); }}
                  onDelete={(id) => deleteLine.mutate(id)} />
              ))}
              <Subtotal label="Total COGS" amount={statement.cogsTotalCents} />
              <Row label="Gross profit" amount={statement.grossProfitCents} strong
                trailing={<MarginBadge pct={statement.grossMarginPct} label="gross margin" />} />
            </section>

            {/* OpEx */}
            <section>
              <GroupHeader title="Operating expenses" />
              {statement.opex.map((l) => (
                <CategoryRow key={l.category} line={l} manual={manualByCat.get(l.category)} canEdit={canEditManual}
                  onAdd={() => { setAddingLine(true); setEditingLine({ id: '', category: l.category, label: '', amountCents: 0 }); }}
                  onEdit={(ml) => { setAddingLine(false); setEditingLine(ml); }}
                  onDelete={(id) => deleteLine.mutate(id)} />
              ))}
              <Subtotal label="Total operating expenses" amount={statement.opexTotalCents} />
            </section>

            {/* Net */}
            <section className="border-t-2 border-slate-900 pt-3">
              <Row label="Net profit" amount={statement.netCents} strong
                trailing={<MarginBadge pct={statement.netMarginPct} label="net margin" />}
                negativeHighlight />
            </section>
          </div>

          {/* Honest callouts */}
          {(statement.uncategorizedVendors.length > 0 || statement.excluded.length > 0) && (
            <div className="px-6 pb-6 space-y-3">
              {statement.uncategorizedVendors.length > 0 && (
                <UncategorizedCallout vendors={statement.uncategorizedVendors} onFix={() => setShowCategorize(true)} />
              )}
              {statement.excluded.length > 0 && (
                <ExcludedNote excluded={statement.excluded} total={statement.excludedTotalCents} />
              )}
            </div>
          )}

          {!canEditManual && (
            <div className="no-print px-6 pb-5 -mt-2 text-xs text-slate-400">
              Pick a single store to add Labor, Rent, or other manual lines. The roll-up sums each store's own lines.
            </div>
          )}
        </div>
      )}

      {/* Store comparison — the same statement per store, side by side */}
      {isMultiStore && (
        <div className="no-print card max-w-3xl mx-auto overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">By store</h2>
            <span className="text-sm text-slate-500">
              {pair ? (
                <>{rangeLabel(pair.a)} <span className="text-slate-400">vs</span> {rangeLabel(pair.b)}</>
              ) : (
                periodLabel
              )}
            </span>
          </div>
          {storeChartRows.length > 0 && (
            <div className="p-4 pb-0">
              <GroupedBars
                data={storeChartRows}
                xKey="store"
                series={[
                  { key: 'revenue', label: 'Revenue', color: barColors.prior },
                  { key: 'net', label: 'Net profit', color: barColors.current },
                ]}
                format={(v) => `$${Math.round(v).toLocaleString('en-US')}`}
                height={220}
              />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="table-header">Store</th>
                  <th className="table-header text-right">Revenue</th>
                  {pair && <th className="table-header text-right">vs prior</th>}
                  <th className="table-header text-right">Net profit</th>
                  {pair && <th className="table-header text-right">vs prior</th>}
                  <th className="table-header text-right">Net margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stores.map((s, i) => {
                  const a = storeStatementsA[i]?.data;
                  const b = pair ? storeStatementsB[i]?.data : null;
                  if (!a) {
                    return (
                      <tr key={s.id}>
                        <td className="table-cell font-medium">{s.name}</td>
                        <td className="table-cell text-right text-slate-400" colSpan={pair ? 5 : 3}>Loading…</td>
                      </tr>
                    );
                  }
                  if (pnlNoData(a)) {
                    return (
                      <tr key={s.id}>
                        <td className="table-cell font-medium">{s.name}</td>
                        <td className="table-cell text-right" colSpan={pair ? 5 : 3}><NoData range={pair?.a} /></td>
                      </tr>
                    );
                  }
                  const bOk = b && !pnlNoData(b) ? b : null;
                  return (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="table-cell font-medium">{s.name}</td>
                      <td className="table-cell text-right tabular-nums">{money(a.revenueCents)}</td>
                      {pair && (
                        <td className="table-cell text-right">
                          {bOk ? (
                            <Delta now={a.revenueCents} prev={bOk.revenueCents} kind="cents" goodWhenUp />
                          ) : b && pnlNoData(b) ? (
                            <NoData range={pair.b} />
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                      )}
                      <td className={clsx('table-cell text-right tabular-nums font-semibold', a.netCents < 0 && 'text-danger-600')}>
                        {money(a.netCents)}
                      </td>
                      {pair && (
                        <td className="table-cell text-right">
                          {bOk ? (
                            <Delta now={a.netCents} prev={bOk.netCents} kind="cents" goodWhenUp />
                          ) : (
                            <span className="text-sm text-slate-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="table-cell text-right tabular-nums">{pct(a.netMarginPct)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manual line editor */}
      {editingLine && (
        <ManualLineModal
          line={editingLine}
          isNew={addingLine}
          categories={categories}
          defaultPeriodKey={statement?.scope.periodKey}
          onClose={() => { setEditingLine(null); setAddingLine(false); }}
          onSave={(d) => {
            if (addingLine) addLine.mutate(d);
            else updateLine.mutate({ id: editingLine.id, d });
          }}
          saving={addLine.isPending || updateLine.isPending}
        />
      )}

      {/* Categorize spend */}
      {showCategorize && (
        <CategorizeModal categories={categories} onClose={() => { setShowCategorize(false); invalidate(); }} />
      )}
    </div>
  );
}

// ---------- statement sub-components ----------

function GroupHeader({ title }: { title: string }) {
  return <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-1">{title}</div>;
}

function Row({ label, amount, strong, note, trailing, negativeHighlight }: {
  label: string; amount: number; strong?: boolean; note?: string; trailing?: React.ReactNode; negativeHighlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="min-w-0">
        <span className={clsx('text-sm', strong ? 'font-semibold text-slate-900' : 'text-slate-700')}>{label}</span>
        {note && <span className="block text-xs text-slate-400">{note}</span>}
      </div>
      <div className="flex items-center gap-3">
        {trailing}
        <span className={clsx('text-sm tabular-nums', strong ? 'font-bold' : 'text-slate-900', negativeHighlight && amount < 0 && 'text-danger-600')}>
          {money(amount)}
        </span>
      </div>
    </div>
  );
}

function Subtotal({ label, amount }: { label: string; amount: number }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-t border-slate-200 mt-1">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      <span className="text-sm font-semibold tabular-nums">{money(amount)}</span>
    </div>
  );
}

function MarginBadge({ pct: p, label }: { pct: number | null; label: string }) {
  return (
    <span className={clsx('badge', p === null ? 'bg-slate-100 text-slate-500' : p >= 0 ? 'badge-success' : 'badge-danger')}>
      {pct(p)} {label}
    </span>
  );
}

function CategoryRow({ line, manual, canEdit, onAdd, onEdit, onDelete }: {
  line: PnlLine; manual?: ManualLine[]; canEdit: boolean;
  onAdd: () => void; onEdit: (l: ManualLine) => void; onDelete: (id: string) => void;
}) {
  return (
    <div className="group py-1">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <span className="text-sm text-slate-700">{line.label}</span>
          <span className="block text-xs text-slate-400">{line.sourceNote}</span>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button onClick={onAdd} title={`Add a manual line under ${line.label}`}
              className="no-print opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-primary-600">
              <Plus className="w-4 h-4" />
            </button>
          )}
          <span className="text-sm tabular-nums text-slate-900">{money(line.amountCents)}</span>
        </div>
      </div>
      {/* Inline manual lines under this category */}
      {canEdit && manual && manual.map((ml) => (
        <div key={ml.id} className="flex items-center justify-between pl-4 py-0.5 text-xs">
          <span className="text-slate-500 inline-flex items-center gap-1">
            ↳ {ml.label || '(manual)'} {ml.periodKey ? <span className="text-slate-400">· {ml.periodKey}</span> : <span className="text-slate-400">· monthly, prorated</span>}
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="tabular-nums text-slate-600">{money(ml.amountCents)}</span>
            <button onClick={() => onEdit(ml)} className="no-print text-slate-400 hover:text-primary-600"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={() => onDelete(ml.id)} className="no-print text-slate-400 hover:text-danger-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </span>
        </div>
      ))}
    </div>
  );
}

function UncategorizedCallout({ vendors, onFix }: { vendors: UncategorizedVendor[]; onFix: () => void }) {
  const total = vendors.reduce((s, v) => s + v.amountCents, 0);
  return (
    <div className="rounded-lg border border-warning-200 bg-warning-50 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-warning-600 mt-0.5 shrink-0" />
        <div className="text-sm text-warning-800">
          <p className="font-medium">{vendors.length} vendor{vendors.length === 1 ? '' : 's'} fell into “Other” ({money(total)}).</p>
          <p className="text-xs mt-0.5">{vendors.slice(0, 5).map((v) => v.vendorName).join(', ')}{vendors.length > 5 ? '…' : ''}</p>
          <button onClick={onFix} className="no-print text-xs font-medium underline mt-1">Categorize these</button>
        </div>
      </div>
    </div>
  );
}

function ExcludedNote({ excluded, total }: { excluded: ExcludedEntry[]; total: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <button onClick={() => setOpen((o) => !o)} className="no-print flex items-center gap-2 text-sm text-slate-600 w-full text-left">
        <Info className="w-4 h-4 text-slate-400" />
        Excluded from expenses: {money(total)} ({excluded.length} vendor{excluded.length === 1 ? '' : 's'}) — {open ? 'hide' : 'show'}
      </button>
      {open && (
        <ul className="mt-2 pl-6 space-y-1">
          {excluded.map((e) => (
            <li key={e.vendorName} className="flex items-center justify-between text-xs text-slate-500">
              <span>{e.vendorName} <span className="text-slate-400">· {e.reason}</span></span>
              <span className="tabular-nums">{money(e.amountCents)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- manual line modal ----------

function ManualLineModal({ line, isNew, categories, defaultPeriodKey, onClose, onSave, saving }: {
  line: ManualLine; isNew: boolean; categories: PnlCategory[]; defaultPeriodKey?: string;
  onClose: () => void; onSave: (d: { category: string; label: string; amountCents: number; periodKey?: string }) => void; saving: boolean;
}) {
  const [category, setCategory] = useState(line.category);
  const [label, setLabel] = useState(line.label);
  const [dollars, setDollars] = useState(line.amountCents ? (line.amountCents / 100).toString() : '');
  const [recurring, setRecurring] = useState<boolean>(!line.periodKey);

  const submit = () => {
    const amountCents = Math.round((parseFloat(dollars) || 0) * 100);
    if (!label.trim() || !amountCents) { toast.error('Add a label and an amount'); return; }
    onSave({ category, label: label.trim(), amountCents, ...(recurring ? {} : { periodKey: line.periodKey || defaultPeriodKey }) });
  };

  return (
    <Modal onClose={onClose} title={isNew ? 'Add a manual line' : 'Edit manual line'}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="input w-full mt-1">
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label} ({c.group === 'cogs' ? 'COGS' : 'OpEx'})</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Label</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Hourly labor" className="input w-full mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Amount (USD)</span>
          <input value={dollars} onChange={(e) => setDollars(e.target.value)} inputMode="decimal" placeholder="0.00" className="input w-full mt-1" />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
          Recurring monthly figure (prorated to the selected range)
        </label>
        <p className="text-xs text-slate-400">
          {recurring ? 'Applies to every period as a monthly amount, scaled by the number of days shown.' : 'Applies only to this period.'}
        </p>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="btn btn-secondary">Cancel</button>
        <button onClick={submit} disabled={saving} className="btn btn-primary inline-flex items-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
        </button>
      </div>
    </Modal>
  );
}

// ---------- categorize spend modal ----------

function CategorizeModal({ categories, onClose }: { categories: PnlCategory[]; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: vendors, isLoading } = useQuery<VendorRow[]>({
    queryKey: ['pnl-vendors'],
    queryFn: async () => { const res = await pnlApi.getVendors(); return res.data?.data || res.data; },
  });
  const setCat = useMutation({
    mutationFn: (p: { vendorName: string; category: string }) => pnlApi.setVendorCategory(p.vendorName, p.category),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pnl-vendors'] }); qc.invalidateQueries({ queryKey: ['pnl-statement'] }); },
    onError: () => toast.error('Could not save that mapping'),
  });

  return (
    <Modal onClose={onClose} title="Categorize spend" wide>
      <p className="text-sm text-slate-500 mb-3">
        Map each vendor to a P&amp;L category, shared across all your stores. Unmapped vendors use a best-guess from their name.
        Pick <strong>Excluded</strong> for things like sales-tax remittance.
      </p>
      {isLoading ? (
        <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-600" /></div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1">
          <table className="w-full">
            <thead className="sticky top-0 bg-white">
              <tr>
                <th className="table-header">Vendor</th>
                <th className="table-header">Category</th>
                <th className="table-header">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(vendors || []).map((v) => (
                <tr key={v.normalizedName} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{v.name}</td>
                  <td className="table-cell">
                    <select
                      value={v.category ?? ''}
                      onChange={(e) => setCat.mutate({ vendorName: v.name, category: e.target.value })}
                      className="input w-auto text-sm"
                    >
                      <option value="" disabled>Choose…</option>
                      <optgroup label="COGS">
                        {categories.filter((c) => c.group === 'cogs').map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </optgroup>
                      <optgroup label="Operating expenses">
                        {categories.filter((c) => c.group === 'opex').map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </optgroup>
                      <option value="excluded">Excluded (not an expense)</option>
                    </select>
                  </td>
                  <td className="table-cell">
                    <span className={clsx('badge', v.categorySource === 'saved' ? 'badge-success' : v.categorySource === 'heuristic' ? 'badge-warning' : 'bg-slate-100 text-slate-500')}>
                      {v.categorySource === 'saved' ? 'set by you' : v.categorySource === 'heuristic' ? 'auto-guess' : 'unmatched'}
                    </span>
                  </td>
                </tr>
              ))}
              {(vendors || []).length === 0 && (
                <tr><td colSpan={3} className="table-cell text-slate-400 text-center py-6">No vendors yet. They appear here once you have invoices.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end mt-4">
        <button onClick={onClose} className="btn btn-primary">Done</button>
      </div>
    </Modal>
  );
}

// ---------- modal shell ----------

function Modal({ children, title, onClose, wide }: { children: React.ReactNode; title: string; onClose: () => void; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className={clsx('bg-white rounded-xl shadow-xl w-full p-5', wide ? 'max-w-2xl' : 'max-w-md')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
